import {useMemo} from 'react';
import Fuse, {FuseOptionKey, IFuseOptions} from 'fuse.js';
import {UnionToIntersection} from 'type-fest';

import {DeepKeys} from 'types';

export type FuseSearchKey<T extends any> = NonNullable<T> extends Array<infer U>
  ? DeepKeys<UnionToIntersection<U>>
  : never;

type Options<T extends any> = Omit<IFuseOptions<T>, 'keys'> & {
  keys?: FuseSearchKey<T>[] | Readonly<FuseSearchKey<T>[]>;
};

const defaultOptions = {
  findAllMatches: true,
  isCaseSensitive: false,
  ignoreFieldNorm: true,
  ignoreLocation: true,
  threshold: 0.1,
};

export function useFuzzySearch<T>(data: T, searchText: string, options: Options<T>): T {
  const fuse = useMemo(
    () => new Fuse(data as any, {...defaultOptions, ...options, keys: options.keys as FuseOptionKey<T>[] | undefined}),
    [data, options]
  );

  return useMemo(() => {
    if (searchText.trim()) {
      return fuse.search(searchText).map(i => i.item);
    }

    return data;
  }, [data, fuse, searchText]) as T;
}
